import { seatToRelative, type TSeat } from '@core/types';
import type { ISelfDrawHandTuning } from '@ui/index';

/**
 * 預覽模式階層：Preview 根層可進入 SelfDraw、Game 或 ActionBar；
 * ActionBar 可再進入 ActionBarExpanded。ESC 一律返回上一層。
 */
export const PreviewLayer = {
  Preview: 'preview',
  SelfDraw: 'self-draw',
  Game: 'game',
  ActionBar: 'action-bar',
  ActionBarExpanded: 'action-bar-expanded',
} as const;

export type TPreviewLayer = (typeof PreviewLayer)[keyof typeof PreviewLayer];

const PREVIEW_SEAT_LABELS = ['本家', '下家', '對家', '上家'] as const;
const SELF_DRAW_POSITION_STEP_PX = 1;
const SELF_DRAW_SPACING_STEP_PX = 1;
const SELF_DRAW_SCALE_STEP = 0.01;
const SELF_DRAW_MIN_SPACING_PX = 1;
const SELF_DRAW_MIN_SCALE = 0.1;
const SELF_DRAW_MAX_SCALE = 2;

export const SelfDrawTuningControl = {
  Left: 'left',
  Right: 'right',
  Up: 'up',
  Down: 'down',
  IncreaseSpacing: 'increase-spacing',
  DecreaseSpacing: 'decrease-spacing',
  ScaleUp: 'scale-up',
  ScaleDown: 'scale-down',
} as const;
export type TSelfDrawTuningControl =
  (typeof SelfDrawTuningControl)[keyof typeof SelfDrawTuningControl];

/** 自摸預覽按 N：依牌局座位順序切換到下一家，第四家後回到第一家。 */
export function nextPreviewSeat(seat: TSeat): TSeat {
  return ((seat + 1) % 4) as TSeat;
}

/** 依玩家所在座位，產生自摸預覽提示使用的相對座位名稱。 */
export function previewSeatLabel(seat: TSeat, playerSeat: TSeat): string {
  return PREVIEW_SEAT_LABELS[seatToRelative(seat, playerSeat)]!;
}

/** 套用一次自摸預覽鍵盤調校，並把小數固定在便於選單顯示／回報的精度。 */
export function adjustSelfDrawPreviewTuning(
  tuning: ISelfDrawHandTuning,
  control: TSelfDrawTuningControl
): ISelfDrawHandTuning {
  const roundedSpacing = (value: number): number => Number(value.toFixed(2));
  const roundedScale = (value: number): number => Number(value.toFixed(3));
  switch (control) {
    case SelfDrawTuningControl.Left:
      return { ...tuning, offsetX: tuning.offsetX - SELF_DRAW_POSITION_STEP_PX };
    case SelfDrawTuningControl.Right:
      return { ...tuning, offsetX: tuning.offsetX + SELF_DRAW_POSITION_STEP_PX };
    case SelfDrawTuningControl.Up:
      return { ...tuning, offsetY: tuning.offsetY - SELF_DRAW_POSITION_STEP_PX };
    case SelfDrawTuningControl.Down:
      return { ...tuning, offsetY: tuning.offsetY + SELF_DRAW_POSITION_STEP_PX };
    case SelfDrawTuningControl.IncreaseSpacing:
      return {
        ...tuning,
        tileSpacing: roundedSpacing(tuning.tileSpacing + SELF_DRAW_SPACING_STEP_PX),
      };
    case SelfDrawTuningControl.DecreaseSpacing:
      return {
        ...tuning,
        tileSpacing: Math.max(
          SELF_DRAW_MIN_SPACING_PX,
          roundedSpacing(tuning.tileSpacing - SELF_DRAW_SPACING_STEP_PX)
        ),
      };
    case SelfDrawTuningControl.ScaleUp:
      return {
        ...tuning,
        tileScale: Math.min(
          SELF_DRAW_MAX_SCALE,
          roundedScale(tuning.tileScale + SELF_DRAW_SCALE_STEP)
        ),
      };
    case SelfDrawTuningControl.ScaleDown:
      return {
        ...tuning,
        tileScale: Math.max(
          SELF_DRAW_MIN_SCALE,
          roundedScale(tuning.tileScale - SELF_DRAW_SCALE_STEP)
        ),
      };
  }
}
